import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Upload, Loader2, Sparkles, Palette, Package } from 'lucide-react';
import { amplifyApi } from '../services/amplifyApi';
import { useAuth } from '../context/AuthContext';
import { useBrand } from '../context/BrandContext';
import LibraryPicker from './LibraryPicker';
import ProductSelector from './ProductSelector';

const ImageUploader = ({ onImageUpload, onBrowseProductSelect }) => {
  const { isAuthenticated, openLoginModal } = useAuth();
  const brand = useBrand();
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [activeTab, setActiveTab] = useState('upload'); // 'upload' | 'ai' | 'library' | 'browse'

  // Browse Products tab state
  const [browseCategories, setBrowseCategories] = useState([]);
  const [browseBlueprints, setBrowseBlueprints] = useState([]);
  const [isBrowseLoading, setIsBrowseLoading] = useState(false);
  const [browseSelection, setBrowseSelection] = useState([]);

  // Feature flags: Check which features are enabled for this brand
  const aiGenerationEnabled = brand.features?.aiGeneration?.enabled !== false;
  const artLibraryEnabled = brand.features?.artLibrary?.enabled !== false;

  const handleFile = async (file) => {
    if (!file || !file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    setIsUploading(true);
    try {
      const imageData = await amplifyApi.uploadImage(file);
      onImageUpload(imageData.image);
    } catch (error) {
      alert('Failed to upload image. Please try again.');
      console.error('Upload error:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleGenerateDesign = async () => {
    if (!aiPrompt.trim()) {
      alert('Please enter a description for your design');
      return;
    }

    // SECURITY: Require login for AI generation
    if (!isAuthenticated) {
      openLoginModal('Sign in to use AI image generation');
      return;
    }

    setIsGenerating(true);
    try {
      const response = await amplifyApi.generateImage(aiPrompt);

      // Handle auth/rate limit errors from backend
      if (response.error === 'AUTH_REQUIRED') {
        openLoginModal('Please sign in to use AI image generation');
        return;
      }

      if (response.error === 'RATE_LIMITED') {
        alert(response.message || 'Rate limit reached. Please try again later.');
        return;
      }

      if (response.success && response.image) {
        // Include the AI prompt with the generated image for search/tagging
        onImageUpload({
          ...response.image,
          aiPrompt: aiPrompt.trim(),
          imageSource: 'ai-generated'
        });
      } else {
        throw new Error(response.error || 'Failed to generate image');
      }
    } catch (error) {
      // Handle errors that come through as exceptions
      const errorData = error.response?.data || {};
      if (errorData.error === 'AUTH_REQUIRED') {
        openLoginModal('Please sign in to use AI image generation');
        return;
      }
      if (errorData.error === 'RATE_LIMITED') {
        alert(errorData.message || 'Rate limit reached. Please try again later.');
        return;
      }
      alert('Failed to generate design. Please try again.');
      console.error('Generation error:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle selection from art library
  const handleLibrarySelect = (artwork) => {
    onImageUpload({
      url: artwork.url,
      urlFallbacks: artwork.urlFallbacks || [],  // Array of fallback URLs for Rawpixel
      name: artwork.name,
      source: artwork.source,
      attribution: artwork.attribution,
      isPublicDomain: artwork.isPublicDomain
    });
  };

  // Lazy-fetch product categories when Browse tab is activated
  useEffect(() => {
    if (activeTab === 'browse' && browseBlueprints.length === 0 && !isBrowseLoading) {
      const fetchBrowseProducts = async () => {
        setIsBrowseLoading(true);
        try {
          const response = await amplifyApi.getProductCategories();
          if (response.categories) {
            setBrowseCategories(
              [...response.categories].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
            );
          }
          if (response.blueprints) {
            setBrowseBlueprints(response.blueprints);
          }
        } catch (error) {
          console.error('Failed to fetch products for browsing:', error);
        } finally {
          setIsBrowseLoading(false);
        }
      };
      fetchBrowseProducts();
    }
  }, [activeTab, browseBlueprints.length, isBrowseLoading]);

  // Build tabs array based on feature flags
  const tabs = useMemo(() => {
    const baseTabs = [
      { id: 'upload', label: 'Upload', icon: Upload },
    ];
    // Only show AI Generate tab if feature is enabled
    if (aiGenerationEnabled) {
      baseTabs.push({ id: 'ai', label: 'AI Generate', icon: Sparkles });
    }
    // Only show Art Library tab if feature is enabled
    if (artLibraryEnabled) {
      baseTabs.push({ id: 'library', label: 'Art Library', icon: Palette });
    }
    // Always show Browse Products tab
    baseTabs.push({ id: 'browse', label: 'Browse Products', icon: Package });
    return baseTabs;
  }, [aiGenerationEnabled, artLibraryEnabled]);

  return (
    <div className="w-full">
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleChange}
        accept="image/*"
        className="hidden"
      />

      {/* Tab Navigation */}
      <div className="flex border-b border-slate-200 mb-6">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-3 font-medium text-sm border-b-2 transition-colors flex-1 sm:flex-none
                ${isActive
                  ? 'border-transparent'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }
              `}
              style={isActive ? { borderBottomColor: brand.colors.primary, color: brand.colors.primary } : {}}
              title={tab.label}
            >
              <Icon size={18} className="flex-shrink-0" />
              <span className="hidden sm:inline">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Upload Tab Content */}
      {activeTab === 'upload' && (
        <div
          className={`relative border-2 border-dashed rounded-xl p-8 sm:p-12 text-center transition-all duration-200 cursor-pointer
            ${dragActive
              ? 'scale-[1.01]'
              : 'border-slate-300 bg-slate-50 hover:bg-slate-100 hover:border-slate-400'
            }
            ${isUploading ? 'pointer-events-none opacity-80' : ''}
          `}
          style={dragActive ? { borderColor: brand.colors.primary, backgroundColor: `${brand.colors.primary}10` } : {}}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => !isUploading && fileInputRef.current.click()}
        >
          <div className="flex flex-col items-center gap-4">
            {isUploading ? (
              <>
                <div className="bg-white p-4 rounded-full shadow-sm">
                  <Loader2 className="w-8 h-8 animate-spin" style={{ color: brand.colors.primary }} />
                </div>
                <p className="text-slate-600 font-medium animate-pulse">Processing your image...</p>
              </>
            ) : (
              <>
                <div className="bg-white p-4 rounded-full shadow-sm">
                  <Upload className="w-8 h-8" style={{ color: brand.colors.primary }} />
                </div>
                <div className="space-y-1">
                  <p className="text-lg font-semibold text-slate-900">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-sm text-slate-500">
                    SVG, PNG, JPG or GIF (max. 10MB)
                  </p>
                </div>
                <button
                  className="btn-primary mt-4"
                  onClick={(e) => {
                    e.stopPropagation();
                    fileInputRef.current.click();
                  }}
                >
                  Select Image
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* AI Generate Tab Content */}
      {activeTab === 'ai' && (
        <div
          className="rounded-xl p-6 border"
          style={{
            background: `linear-gradient(to right, ${brand.colors.secondary || brand.colors.primary}10, ${brand.colors.primary}10)`,
            borderColor: `${brand.colors.secondary || brand.colors.primary}20`
          }}
        >
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5" style={{ color: brand.colors.secondary || brand.colors.primary }} />
            <span className="font-medium text-slate-800">AI Design Generator</span>
          </div>
          <div className="flex flex-col gap-3">
            <input
              type="text"
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder="Describe your design... (e.g., 'a cute cat wearing sunglasses')"
              className="w-full px-4 py-3 rounded-lg border border-slate-200 outline-none transition-all"
              style={{ '--tw-ring-color': `${brand.colors.primary}30` }}
              disabled={isGenerating || isUploading}
              onKeyDown={(e) => e.key === 'Enter' && handleGenerateDesign()}
              onFocus={(e) => {
                e.target.style.borderColor = brand.colors.primary;
                e.target.style.boxShadow = `0 0 0 3px ${brand.colors.primary}20`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '';
                e.target.style.boxShadow = '';
              }}
            />
            <button
              className="w-full px-4 py-3 text-white rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{
                background: `linear-gradient(to right, ${brand.colors.secondary || brand.colors.primary}, ${brand.colors.primary})`
              }}
              onClick={handleGenerateDesign}
              disabled={isGenerating || isUploading || !aiPrompt.trim()}
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  Generate Design
                </>
              )}
            </button>
          </div>
          <p className="text-sm text-slate-500 mt-4">
            Imagine it. We'll create it. Describe your vision and watch it come to life.
          </p>
          {!isAuthenticated && (
            <p className="text-xs text-amber-600 mt-2">
              Sign in required for AI generation
            </p>
          )}
        </div>
      )}

      {/* Art Library Tab Content */}
      {activeTab === 'library' && artLibraryEnabled && (
        <LibraryPicker onSelectImage={handleLibrarySelect} />
      )}

      {/* Browse Products Tab Content */}
      {activeTab === 'browse' && (
        <div className="space-y-4">
          <div
            className="rounded-xl p-4 border"
            style={{ backgroundColor: `${brand.colors.primary}08`, borderColor: `${brand.colors.primary}20` }}
          >
            <div className="flex items-center gap-2 mb-1">
              <Package className="w-5 h-5" style={{ color: brand.colors.primary }} />
              <span className="font-medium text-slate-800">Browse Available Products</span>
            </div>
            <p className="text-sm text-slate-500">
              See what's available, then upload your design to apply to your selected products.
            </p>
          </div>

          <ProductSelector
            categories={browseCategories}
            blueprints={browseBlueprints}
            selectedCategories={browseSelection}
            onSelectionChange={(selected) => {
              setBrowseSelection(selected);
              if (onBrowseProductSelect) {
                onBrowseProductSelect({
                  selectedIds: selected,
                  // Pass info about the most recently selected blueprint for the context banner
                  blueprintId: selected.length > 0 ? selected[selected.length - 1] : null,
                  productTitle: selected.length > 0
                    ? browseBlueprints.find(b => String(b.id) === selected[selected.length - 1])?.title || null
                    : null,
                  mockupUrl: selected.length > 0
                    ? browseBlueprints.find(b => String(b.id) === selected[selected.length - 1])?.image || null
                    : null,
                  count: selected.length
                });
              }
            }}
            isLoading={isBrowseLoading}
          />

          {browseSelection.length > 0 && (
            <div
              className="rounded-xl p-4 text-center border"
              style={{ backgroundColor: `${brand.colors.primary}10`, borderColor: `${brand.colors.primary}30` }}
            >
              <p className="font-medium" style={{ color: brand.colors.primaryDark || brand.colors.primary }}>
                {browseSelection.length} product{browseSelection.length !== 1 ? 's' : ''} selected! Now upload your design.
              </p>
              <button
                onClick={() => setActiveTab('upload')}
                className="mt-2 px-4 py-2 text-white rounded-lg text-sm font-medium transition-colors"
                style={{ backgroundColor: brand.colors.primary }}
                onMouseEnter={(e) => e.target.style.backgroundColor = brand.colors.primaryHover || brand.colors.primary}
                onMouseLeave={(e) => e.target.style.backgroundColor = brand.colors.primary}
              >
                Upload Design
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
